import { useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getBackupData } from 'zite-endpoints-sdk';
import {
  Download, Users, BookOpen, ShieldCheck, Flame,
  Calendar, CheckSquare, Wrench, HardDrive, RefreshCw, Info, Archive,
} from 'lucide-react';
import { getCached, setCached, formatCacheAge } from '../lib/localCache';

interface Category {
  id: 'staff' | 'training' | 'compliance' | 'fire_logs' | 'events' | 'checklists' | 'maintenance';
  label: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  color: string;
}

const CATEGORIES: Category[] = [
  { id: 'staff', label: 'Staff Records', description: 'Names, roles, departments, employment type, start dates, onboarding status', icon: Users, color: 'text-blue-500' },
  { id: 'training', label: 'Training & Certificates', description: 'Module progress, quiz scores, completion dates, issued certificates', icon: BookOpen, color: 'text-emerald-500' },
  { id: 'compliance', label: 'Compliance Submissions', description: 'Compliance form status, quiz scores, signed dates, deadlines', icon: ShieldCheck, color: 'text-purple-500' },
  { id: 'fire_logs', label: 'Fire Check Logs', description: 'All fire safety checks, results, locations, actions needed', icon: Flame, color: 'text-red-500' },
  { id: 'events', label: 'Events', description: 'Event titles, dates, times, locations, guest counts, notes', icon: Calendar, color: 'text-orange-500' },
  { id: 'checklists', label: 'Checklist Completions', description: 'Completed checklists, status, sign-off records, manager notes', icon: CheckSquare, color: 'text-sky-500' },
  { id: 'maintenance', label: 'Maintenance Log', description: 'All maintenance tasks, categories, priorities, resolution notes', icon: Wrench, color: 'text-amber-500' },
];

type LoadingState = { [key: string]: boolean };
type LastExport = { [key: string]: { filename: string; rows: number; at: Date } };

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function DataBackup() {
  const { user, isLoading } = useAuth();
  const isManager = user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'Owner';

  const [loading, setLoading] = useState<LoadingState>({});
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [lastExport, setLastExport] = useState<LastExport>(() => {
    const cached = getCached<LastExport>('backup_last_export');
    if (cached) return cached.data;
    return {};
  });

  if (isLoading) return null;
  if (!isManager) return <Navigate to="/" replace />;

  const recordExport = (id: string, filename: string, rows: number) => {
    setLastExport(prev => {
      const next = { ...prev, [id]: { filename, rows, at: new Date() } };
      setCached('backup_last_export', next);
      return next;
    });
  };

  const handleDownload = async (cat: Category) => {
    setLoading(prev => ({ ...prev, [cat.id]: true }));
    try {
      const result = await getBackupData({ category: cat.id });
      downloadCsv(result.csv, result.filename);
      recordExport(cat.id, result.filename, result.rowCount);
      toast.success(`Downloaded ${result.rowCount} ${cat.label} records`);
    } catch (e: any) {
      toast.error(e.message || `Failed to export ${cat.label}`);
    } finally {
      setLoading(prev => ({ ...prev, [cat.id]: false }));
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    let successCount = 0;
    for (const cat of CATEGORIES) {
      try {
        const result = await getBackupData({ category: cat.id });
        // Stagger downloads slightly so browsers don't block them
        await new Promise(r => setTimeout(r, 400));
        downloadCsv(result.csv, result.filename);
        recordExport(cat.id, result.filename, result.rowCount);
        successCount++;
      } catch {
        toast.error(`Failed to export ${cat.label}`);
      }
    }
    setDownloadingAll(false);
    if (successCount > 0) {
      toast.success(`Downloaded ${successCount} export files`);
    }
  };

  const totalLastExports = Object.keys(lastExport).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-xl font-bold text-foreground">Data Backup</h1>
          </div>
          <p className="text-sm text-muted-foreground ml-12">
            Export operational data as timestamped CSV files. Save to Google Drive, OneDrive, or any cloud storage for safekeeping.
          </p>
        </div>

        {/* Info banner */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
          <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong className="text-foreground">How to back up to Google Drive or OneDrive:</strong> Download the files below, then manually upload them to your cloud storage folder. We recommend a regular backup schedule (weekly for compliance and fire logs, monthly for staff records).</p>
          </div>
        </div>

        {/* Download All */}
        <div className="bg-card border border-border rounded-xl p-4 mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Archive className="w-5 h-5 text-foreground" />
            <div>
              <p className="text-sm font-semibold text-foreground">Download Full Backup</p>
              <p className="text-xs text-muted-foreground">Exports all 7 categories as separate CSV files</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {totalLastExports > 0 && (
              <span className="text-xs text-muted-foreground hidden sm:block">
                Last backup: {formatCacheAge(Object.values(lastExport).sort((a, b) => b.at.getTime() - a.at.getTime())[0]?.at)}
              </span>
            )}
            <Button onClick={handleDownloadAll} disabled={downloadingAll} className="gap-2">
              {downloadingAll ? (
                <><RefreshCw className="w-4 h-4 animate-spin" />Exporting…</>
              ) : (
                <><Download className="w-4 h-4" />Download All</>
              )}
            </Button>
          </div>
        </div>

        {/* Category cards */}
        <div className="grid gap-3 sm:grid-cols-2">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const last = lastExport[cat.id];
            const isExporting = loading[cat.id];

            return (
              <div
                key={cat.id}
                className="bg-card border border-border rounded-xl p-4 flex items-start gap-4 hover:border-primary/30 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className={`w-4.5 h-4.5 ${cat.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-foreground">{cat.label}</p>
                    {last && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-muted-foreground">
                        {last.rows} rows
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">{cat.description}</p>
                  <div className="flex items-center justify-between">
                    {last ? (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        Last exported {formatCacheAge(new Date(last.at))}
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Not yet exported</span>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1.5"
                      disabled={isExporting}
                      onClick={() => handleDownload(cat)}
                    >
                      {isExporting ? (
                        <><RefreshCw className="w-3 h-3 animate-spin" />Exporting…</>
                      ) : (
                        <><Download className="w-3 h-3" />Export CSV</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="text-[11px] text-muted-foreground text-center mt-6 pb-4">
          Exports are point-in-time snapshots. Sensitive fields (bank details, NI numbers) are excluded from staff exports.
          All exports are restricted to Manager, Admin, and Owner roles.
        </p>
      </div>
    </div>
  );
}
